import { useCallback, useMemo } from 'react';

import Api, { RuntimeConfig, useRequest } from '@/api';

interface WebFormResult {
  id: string;
  description: string;
  links: {
    download_link: string;
    upload_link: string;
  };
  metadata: {
    flow_id?: string;
  };
}

export function useWebFormUpload() {
  const { run: runRequest, loading: requestLoading } = useRequest<WebFormResult, { type: string }>(
    Api.webFormsAttachmentRequest,
  );
  const { run: runUpload, loading: uploadLoading, cancel } = useRequest<null, File>(Api.assetsUploadFile);
  const { run: runCreate, loading: createLoading } = useRequest<
    WebFormResult,
    { name: string; id: string; description: string; size: number }
  >(Api.webFormsAttachmentCreate);
  const { run: runDelete } = useRequest(Api.webFormsAttachmentDelete);
  const loading = useMemo(
    () => requestLoading || uploadLoading || createLoading,
    [requestLoading, uploadLoading, createLoading],
  );

  const uploadFile = useCallback(
    async (file: File, attachmentFlowId: string, onProgress?: (event: { percent: number | string }) => void) => {
      const headers = attachmentFlowId ? { 'attachment-flow-id': attachmentFlowId } : undefined;

      const requestResp = await runRequest(
        { type: file.type },
        RuntimeConfig.create({
          headers,
          onUploadProgress: ({ total, loaded }) => {
            onProgress?.({ percent: Math.round((loaded / total) * 5).toFixed(2) });
          },
        }),
      );
      if (!requestResp) return;

      await runUpload(
        JSON.parse(requestResp?.links.upload_link).href,
        file,
        RuntimeConfig.create({
          onUploadProgress: ({ total, loaded }) => {
            onProgress?.({ percent: Math.round((loaded / total) * 90).toFixed(2) + 5 });
          },
        }),
      );

      const createResult = await runCreate(
        {
          name: `${file.name}`,
          id: requestResp?.id,
          description: requestResp?.description,
          size: file.size,
        },
        RuntimeConfig.create({
          headers,
          onUploadProgress: ({ total, loaded }) => {
            onProgress?.({ percent: Math.round((loaded / total) * 5).toFixed(2) + 95 });
          },
        }),
      );

      return createResult?.id;
    },
    [runCreate, runRequest, runUpload],
  );

  const deleteFile = useCallback(
    async (id: string, attachmentFlowId: string) => {
      const headers = attachmentFlowId ? { 'attachment-flow-id': attachmentFlowId } : undefined;

      const deleteResp = await runDelete(id, RuntimeConfig.create({ headers }));
      return deleteResp;
    },
    [runDelete],
  );

  return {
    uploadFile,
    deleteFile,
    loading,
    cancel,
  };
}
